import { now, year, month, day } from './helpers/date'
import type { Event } from './types'
import fetch from 'cross-fetch'

const logger = useLogger('analytics')

export const DEFAULT_BASE_URL = 'https://staging.monito.com'
export const ENDPOINT = 'event'

// Setting Content-Type + POST to ensure to have a simple request avoiding the need of a preflighted request (CORS)
// not having the OPTIONS will increase perf.
const SIMPLE_REQUEST_OPTIONS = {
  method: 'POST',
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
}

function prepare(data: Event): string {
  const jsonString = JSON.stringify({ ...data, sentAt: now() })

  return JSON.stringify({
    data: import.meta.client
      ? btoa(unescape(encodeURIComponent(jsonString)))
      : Buffer.from(unescape(encodeURIComponent(jsonString)), 'utf-8').toString('base64'),
  })
}

export function messagePath(event: Event): string {
  const d = new Date()
  return [ENDPOINT, [year(d), month(d), day(d)].join(''), event.messageId].join('/')
}

export async function send(
  event: Event,
  server: string | undefined = DEFAULT_BASE_URL,
): Promise<boolean> {
  logger.debug({ event }, `Sending event ${event.messageId}`)
  if (process.env.NODE_ENV === 'development') {
    return Promise.resolve(false)
  }

  if (import.meta.server || typeof navigator.sendBeacon !== 'function') {
    return fallback(event, server)
  }

  const queued = navigator.sendBeacon(`${server}/${messagePath(event)}`, prepare(event))
  if (queued) {
    logger.debug(`Event ID ${event.messageId} queued to be sent`)
  } else {
    logger.warn(`The "sendBeacon" queueing failed for event ID ${event.messageId}`)
  }
  return queued
}

async function fallback(event: Event, server: string): Promise<boolean> {
  try {
    const response = await fetch(`${server}/${messagePath(event)}`, {
      ...SIMPLE_REQUEST_OPTIONS,
      body: prepare(event),
    })
    if (response.status >= 200 && response.status <= 299) {
      logger.debug(`Sent event ID ${event.messageId}`)
      return true
    }
    logger.error(response, `Could not send the event ID ${event.messageId} to analytics API`)
  } catch (error) {
    logger.error(error, `Error while sending the event ID ${event.messageId} to analytics API`)
  }
  return false
}
